import { AxiosError } from 'axios';
import { IErrorResponse, IApiErrorResponse } from '../interface/main.interface';

/**
 * Normalize an Axios error to IErrorResponse for Redux thunk rejectWithValue.
 * Handles various error response formats from the backend and provides a consistent
 * error response structure.
 *
 * @param err - The caught error (typically unknown type from try/catch)
 * @param fallbackMessage - Default message to use if no message is extracted from the error
 * @returns IErrorResponse with normalized status and message
 */
export function normalizeAxiosError(err: unknown, fallbackMessage: string): IErrorResponse {
  const error = err as AxiosError<IApiErrorResponse>;
  const errorData = error.response?.data || (error as IApiErrorResponse);

  // The api.tsx interceptors reject with the response BODY (not an AxiosError),
  // so the HTTP status may only be available as `status` on the body itself.
  const bodyStatus = typeof errorData?.status === 'number' ? errorData.status : undefined;

  return {
    status: error.response?.status || bodyStatus || errorData?.statusCode || 500,
    message: Array.isArray(errorData?.message)
      ? errorData.message.join(', ')
      : errorData?.message || fallbackMessage,
    error: errorData?.error,
    // Carry the structured fields through. Dropping them here silently defeated
    // every caller that branches on `errorCode` — the auth lockout screens
    // matched nothing and rendered no lock, because by the time the thunk called
    // rejectWithValue the code and its payload were already gone.
    ...(errorData?.errorCode !== undefined && { errorCode: errorData.errorCode }),
    ...(errorData?.data !== undefined && { data: errorData.data }),
  };
}
